import { NetworkStatus } from "@apollo/client";
import { fromUnixTime } from "date-fns/esm";

import { PollingIntervalGroups } from "apollo/constants";
import usePolledQuery from "apollo/usePolledQuery";
import DocumentationButton from "components/DocumentationButton";
import MetaInfoListItem from "components/MetaInfoList/Item";
import MissingDataBanner from "components/MissingDataBanner";
import { ArrowsLeftRight, EmptystateCalendarClockColored, Stack } from "components/icons/generated";
import Box from "ds/components/Box";
import EmptyState from "ds/components/EmptyState";
import IconEmphasized from "ds/components/IconEmphasized";
import Link from "ds/components/Link";
import TileContent from "ds/components/Tile/Content";
import TileWrapper from "ds/components/Tile/Wrapper";
import Typography from "ds/components/Typography";
import { AnalyticsPageDashboard } from "hooks/useAnalytics/pages/dashboard";
import { formatDate } from "utils/date";
import { getDocsUrl } from "utils/getDocsUrl";

import DashboardWidgetsNextDriftDetectionScheduleSkeleton from "./Skeleton";
import { GET_UPCOMING_DRIFT_DETECTION_SCHEDULES } from "./gql";
import styles from "./styles.module.css";
import { GetUpcomingDriftDetectionSchedulesGql } from "./types";

const DashboardWidgetsNextDriftDetectionSchedule = () => {
  const { data, loading, error, refetch, networkStatus } =
    usePolledQuery<GetUpcomingDriftDetectionSchedulesGql>(GET_UPCOMING_DRIFT_DETECTION_SCHEDULES, {
      pollingGroup: PollingIntervalGroups.Dashboard,
    });

  if (error) {
    return (
      <MissingDataBanner
        text="Couldn’t load drift detection schedule. Please try to refresh or come back later."
        refreshHandler={refetch}
      />
    );
  }

  const isLoading =
    (loading && !data?.metrics?.upcomingDriftDetectionSchedules) ||
    networkStatus === NetworkStatus.refetch;

  if (isLoading) {
    return <DashboardWidgetsNextDriftDetectionScheduleSkeleton />;
  }

  const schedules = data?.metrics?.upcomingDriftDetectionSchedules?.filter(
    // eslint-disable-next-line @cspell/spellchecker
    ({ driftDetetionIntegration }) => !!driftDetetionIntegration.nextSchedule
  );
  const hasNoSchedule = !schedules?.length;

  return (
    <Box direction="column" fullWidth>
      {hasNoSchedule && (
        <TileWrapper>
          <Box padding="small" fullWidth justify="center">
            <EmptyState
              title="You haven't created any schedules yet"
              icon={EmptystateCalendarClockColored}
              caption="Navigate to Stack view / Scheduling to create your first schedule."
            >
              <Box gap="medium">
                <DocumentationButton to={getDocsUrl("/concepts/stack/drift-detection.html")} />
                {/* TODO: [dashboard] userflow trigger */}
                {/* <Button variant="primary">Show me how</Button> */}
              </Box>
            </EmptyState>
          </Box>
        </TileWrapper>
      )}

      {!hasNoSchedule && (
        <Box direction="column" gap="medium">
          {/* eslint-disable-next-line @cspell/spellchecker */}
          {schedules.map(({ driftDetetionIntegration, stackTile }, i) => (
            <TileWrapper key={i}>
              <TileContent gap="small">
                <Box gap="large" align="start" fullWidth>
                  <IconEmphasized icon={ArrowsLeftRight} />
                  <Box direction="column" gap="small" align="start" className={styles.content}>
                    <Link
                      analyticsTitle="Next Drift Detection Schedule Widget - Schedule Clicked"
                      analyticsPage={AnalyticsPageDashboard.Dashboard}
                      variant="secondary"
                      href={`/stack/${stackTile.slug}/scheduling`}
                      size="small"
                    >
                      <Typography tag="span">
                        {formatDate({
                          // eslint-disable-next-line @cspell/spellchecker
                          date: fromUnixTime(driftDetetionIntegration.nextSchedule!),
                          // eslint-disable-next-line @cspell/spellchecker
                          timezone: driftDetetionIntegration.timezone,
                        })}
                      </Typography>
                    </Link>

                    <MetaInfoListItem
                      fullWidth
                      analyticsTitle="Next Drift Detection Schedule Widget - Stack Clicked"
                      analyticsPage={AnalyticsPageDashboard.Dashboard}
                      to={`/stack/${stackTile.slug}`}
                      linkText={stackTile.name}
                      icon={Stack}
                      variant="p-body3"
                    />
                  </Box>
                </Box>
              </TileContent>
            </TileWrapper>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default DashboardWidgetsNextDriftDetectionSchedule;
